import React, { useEffect, useState, useRef } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  Table,
  VisibilityState,
  SortingState,
} from '@tanstack/react-table';
import {
  Table as TableComponent,
  TableCell,
  TableContainer,
  TableHeader,
  ClickableRow,
  FilterBar,
  FilterBarRow,
  TableResultCount,
  SearchResultsBanner,
  DEFAULT_COLUMN_SIZE,
  DATE_COLUMN_SIZE,
  HeaderSortingIndicator,
  DateCellWithStatus,
} from '../Table';
import { TableMultiselectFilter } from 'design-system/src/components/TableDropdownFilter/TableDropdownFilter';
import type {
  MultiselectAutocompleteOption,
  MultiselectAutocompleteHandle,
} from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import { getRxFilePreviewUrl, getRxFileDownloadUrl, RxTableResponseRow, StatusInfo, DrugCategory } from '../../api';
import { useNavigate } from 'react-router-dom';
import { Pagination } from 'design-system/src/components/Pagination/Pagination';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PaginationTableProps } from 'design-system/src/components/Pagination/Pagination';
import { emptyTableCellCopy } from 'design-system/src/sharedCopy';
import NotificationDot from 'design-system/src/components/NotificationDot/NotificationDot';
import { PortalTableFilterOptions } from '../useFetchPortalTableFilters';
import { RxFilterValues, RxStatus } from '../../types';
import {
  getAddressString,
  getDropdownInitialValueFromSearchParams,
  getDropdownInitialValueFromSearchParamsAndCookie,
  updateTableColumnFilterValueCookie,
  areStringArraysEqual,
} from '../../utils';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import EyeIcon from 'design-system/src/assets/icons/eye.svg?react';
import styled, { css } from 'styled-components';
import FilePreviewModal from 'design-system/src/components/FilePreviewModal/FilePreviewModal';
import { IconButton } from 'design-system/src/components/Button/Button';
import DownloadTray from 'design-system/src/assets/icons/downloadTray.svg?react';
import Paperclip from 'design-system/src/assets/icons/paperclip.svg?react';
import Checkmark from 'design-system/src/assets/icons/checkmark.svg?react';
import { breakpoints } from 'design-system/src/tokens/breakpoints';
import { DRUG_CATEGORY_DROPDOWN_OPTIONS, STATUS_DROPDOWN_OPTIONS } from '../../constants';
import { TableNullState } from '../TableNullState/TableNullState';
import FormattedCell from 'design-system/src/components/FormattedCell/FormattedCell';
import { AuthContext } from '../../auth';
import DateFilter, { DateFilterHandle } from '../DateFilter';
import { getRxTableNullStateType } from './utils';
import SaveFiltersModal from '../SaveFiltersModal/SaveFiltersModal';
import { SaveFiltersButton } from 'design-system/src/components/SaveFiltersButton/SaveFiltersButton';
import { createTrackingProps } from 'shared-utils/analytics';
import SendFirstPrescriptionModal from '../onboarding/onboardingStepModals/SendFirstPrescriptionModal';

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background-color: var(--grayscale-100);
  margin-left: 8px;
`;

const MergedRowItem = styled.td`
  text-align: center;
  padding: 2rem;
`;

const EnrollmentCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-wrap: nowrap;
`;

const statusContainerStyles = css`
  color: var(--grayscale-900);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
`;

const tableLinkStyles = css`
  text-decoration: none;
  border-bottom: 1px dashed var(--grayscale-900);
  width: fit-content; // stops the underline from extending to the end of the cell
`;

const PreviewFileContainer = styled.a`
  ${statusContainerStyles}
  ${tableLinkStyles}

  // We don't display the preview on mobile because users don't see the native pdf frame
  // with the download button on ios and can't scroll between pages
  @media (max-width: ${breakpoints.tabletMax}px) {
    display: none;
  }
`;

const DownloadFileContainer = styled.a`
  ${statusContainerStyles}
  ${tableLinkStyles}

  // We display the download button instead of a preview button on mobile because users
  // don't see the native pdf frame with the download button on ios
  @media (min-width: ${breakpoints.smallDesktopMin}px) {
    display: none;
  }
`;

const columnHelper = createColumnHelper<RxTableResponseRow>();

const LoadingComponent = ({ table }: { table: Table<RxTableResponseRow> }) => {
  return (
    <tr>
      <MergedRowItem colSpan={table.getAllColumns().length}>
        <LoadingSpinner height="200px" />
      </MergedRowItem>
    </tr>
  );
};

interface RxTableProps {
  data: Array<RxTableResponseRow>;
  loading: boolean;
  tableProps: PaginationTableProps & {
    submitSearchValue: (search: string) => void;
    filters: PortalTableFilterOptions;
    setPrescriberFilter: (prescriberNamesList: string[]) => void;
    setOfficeFilter: (officeNamesList: string[]) => void;
    setDrugNameFilter: (drugNamesList: string[]) => void;
    setDrugCategoryFilter: (drugCategoriesList: DrugCategory[]) => void;
    setStatusFilter: (statusList: RxStatus[]) => void;
    setDateRangeFilter: (dates: { startDate?: string; endDate?: string }) => void;
    sorting: SortingState;
    setSorting: (state: SortingState) => void;
  };
  initialSearchValue?: string;
  initialFilterValues?: RxFilterValues;
  handleDownload: () => void;
  downloadLoading: boolean;
  tabPanelId: string;
  tabId: string;
  onClearSearch?: () => void;
  hasProcessingPrescriptions?: boolean;
  hasAnyPrescriptions?: boolean;
}

const SentToPharmacy = styled.div`
  display: inline;
  white-space: nowrap;
`;

const StyledCheckmark = styled(Checkmark)`
  margin-left: 6px;
`;

const StatusCell = ({
  statusInfo,
  rxId,
  fileHeader,
  type,
}: {
  statusInfo: StatusInfo;
  rxId: string;
  fileHeader?: string;
  type?: 'Hub' | 'Bridge' | 'PAP';
}) => {
  if (statusInfo.status === '') {
    return '';
  }

  const status = type ? `${type} ${statusInfo.status.toLowerCase()}` : statusInfo.status;

  if (statusInfo.status === 'Awaiting office') {
    return <NotificationDot>{status}</NotificationDot>;
  } else if (statusInfo.status === 'Sent to pharmacy') {
    return (
      <SentToPharmacy>
        {statusInfo.status}
        <StyledCheckmark color="var(--green-400)" aria-hidden />
      </SentToPharmacy>
    );
  } else {
    return <StatusDownloadCell status={status} file_id={statusInfo.file_id} rx_id={rxId} file_header={fileHeader} />;
  }
};

const StatusDownloadCell: React.FC<{
  file_id: string | null;
  status: string;
  rx_id: string;
  file_header?: string;
}> = ({ file_id, status, rx_id, file_header }) => {
  const [isPreviewVisible, setPreviewVisible] = useState(false);

  if (!file_id) {
    // force each status string onto its own line in case there are multiple in one cell
    return <div>{status}</div>;
  }

  const handleFilePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewVisible(true);
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);
  };

  return (
    // The preview and download components are both rendered, but only one is visible at a time
    // using CSS breakpoints.
    <>
      <PreviewFileContainer onClick={handleFilePreview}>
        {status}
        <Tooltip content="Open document" childNotInteractive={true}>
          <EyeIcon title="" />
        </Tooltip>

        {isPreviewVisible && (
          <FilePreviewModal
            fileUrl={getRxFilePreviewUrl(rx_id, file_id)}
            onClose={handleClosePreview}
            fileHeader={file_header}
            downloadUrl={getRxFileDownloadUrl(rx_id, file_id)}
          />
        )}
      </PreviewFileContainer>

      <DownloadFileContainer href={getRxFileDownloadUrl(rx_id, file_id)} download onClick={(e) => e.stopPropagation()}>
        {status}

        <Tooltip content="Download document" childNotInteractive={true}>
          <Paperclip aria-hidden />
        </Tooltip>
      </DownloadFileContainer>
    </>
  );
};

const RxTable: React.FC<RxTableProps> = ({
  data,
  tableProps,
  loading,
  initialSearchValue,
  initialFilterValues,
  handleDownload,
  downloadLoading,
  tabPanelId,
  tabId,
  onClearSearch,
  hasProcessingPrescriptions,
  hasAnyPrescriptions,
}) => {
  const navigate = useNavigate();
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [pageLoading, setPageLoading] = useState(loading);
  const [dataState, setDataState] = useState(data);
  const officeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const prescriberFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const drugFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const drugCategoryFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const statusFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const dateFilterRef = useRef<DateFilterHandle>(null);
  const authContext = React.useContext(AuthContext);
  const initialPrescriberOptions = getDropdownInitialValueFromSearchParams(
    authContext.user?.default_filters,
    initialFilterValues?.prescriber,
    'providers',
    tableProps.filters.prescribers,
  ) as MultiselectAutocompleteOption[];
  const initialDrugOptionsFromCookie = getDropdownInitialValueFromSearchParamsAndCookie(
    initialFilterValues?.drug,
    authContext.user?.member_id || null,
    'drug_name',
    tableProps.filters.drugs,
  ) as MultiselectAutocompleteOption[];
  const initialDrugOptions = initialDrugOptionsFromCookie.length
    ? initialDrugOptionsFromCookie
    : (getDropdownInitialValueFromSearchParams(
        authContext.user?.default_filters,
        initialFilterValues?.drug,
        'drugs',
        tableProps.filters.drugs,
      ) as MultiselectAutocompleteOption[]);
  const [prescriberSelections, setPrescriberSelections] = useState<string[]>(
    initialPrescriberOptions.map((o) => o.value),
  );
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSendPrescriptionModal, setShowSendPrescriptionModal] = useState(false);
  const defaultPrescriberNpis = authContext.user?.default_filters?.providers.map((p) => p.npi) || [];
  const showSaveButton = !areStringArraysEqual(prescriberSelections, defaultPrescriberNpis);

  const nextTick = () => new Promise((resolve) => setTimeout(resolve, 0));

  const clearAllFilters = async () => {
    // Clear search input and trigger loading state
    setSearchValue('');
    setPageLoading(true);
    tableProps.submitSearchValue('');

    // Clear TableMultiselectFilter components
    officeFilterRef.current?.clear();
    // We need to pause between clearing each filter
    await nextTick();
    prescriberFilterRef.current?.clear();
    await nextTick();
    drugFilterRef.current?.clear();
    await nextTick();
    drugCategoryFilterRef.current?.clear();
    await nextTick();
    statusFilterRef.current?.clear();
    await nextTick();
    dateFilterRef.current?.clear();

    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'office_name');
    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'drug_name');
    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'drug_category');
  };

  const handleWindowResize = () => {
    setColumnVisibility({
      prescriber_name: window.innerWidth >= breakpoints.largeDesktopMin,
    });
  };

  useEffect(() => {
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const columns = [
    columnHelper.accessor('prescription_recieved_at', {
      header: 'Date',
      size: DATE_COLUMN_SIZE,
      cell: (info) => (
        <DateCellWithStatus
          date={info.getValue()}
          statusLabel={initialSearchValue && info.row.original.portal_status === 'closed' ? 'Closed' : undefined}
        />
      ),
      enableSorting: true,
    }),
    columnHelper.accessor(
      (row) => {
        return { name: row.patient_name, patient_dob: row.patient_dob };
      },
      {
        id: 'patient_info',
        header: 'Patient info',
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            title={info.getValue().name}
            subtitle={`DOB: ${formatStandardDate(info.getValue().patient_dob)}`}
          />
        ),
        size: DEFAULT_COLUMN_SIZE,
        enableSorting: true,
      },
    ),
    columnHelper.accessor('drug_name', {
      header: 'Drug',
      cell: (info) => (
        <FormattedCell
          id={info.row.original.id}
          title={info.getValue()}
          subtitle={info.row.original.name}
          truncateBody
        />
      ),
      enableSorting: true,
    }),
    columnHelper.accessor('pa_appeal_status', {
      header: 'Coverage',
      cell: (info) =>
        info.getValue()?.status ? (
          <StatusCell statusInfo={info.getValue()} rxId={info.row.original.id} fileHeader="PA Letter" />
        ) : (
          emptyTableCellCopy
        ),
      size: DEFAULT_COLUMN_SIZE,
      enableSorting: false,
    }),
    columnHelper.accessor(
      (row) => {
        return {
          hubStatus: row.hub_status,
          bridgeStatus: row.bridge_status,
          papStatus: row.pap_status,
        };
      },
      {
        id: 'enrollment',
        header: 'Enrollment',
        cell: (info) => {
          return info.getValue().hubStatus.status !== '' ||
            info.getValue().bridgeStatus.status !== '' ||
            info.getValue().papStatus.status !== '' ? (
            <EnrollmentCell>
              <StatusCell
                statusInfo={info.getValue().hubStatus}
                rxId={info.row.original.id}
                fileHeader="Hub Form"
                type="Hub"
              />
              <StatusCell
                statusInfo={info.getValue().bridgeStatus}
                rxId={info.row.original.id}
                fileHeader="Bridge Form"
                type="Bridge"
              />
              <StatusCell
                statusInfo={info.getValue().papStatus}
                rxId={info.row.original.id}
                fileHeader="Patient Assistance (PAP) Letter"
                type="PAP"
              />
            </EnrollmentCell>
          ) : (
            emptyTableCellCopy
          );
        },
        size: DEFAULT_COLUMN_SIZE,
        enableSorting: false,
      },
    ),
    columnHelper.accessor('pharmacy_status', {
      header: 'Pharmacy',
      cell: (info) => {
        const cellComponent = info.getValue().status ? (
          <StatusCell statusInfo={info.getValue()} rxId={info.row.original.id} />
        ) : (
          <span>{emptyTableCellCopy}</span>
        );
        if (info.getValue().status && info.row.original.pharmacy_address) {
          const addressString = getAddressString(info.row.original.pharmacy_address);
          return (
            <Tooltip content={addressString} childNotInteractive={true}>
              {cellComponent}
            </Tooltip>
          );
        }
        return cellComponent;
      },
      size: DEFAULT_COLUMN_SIZE,
      enableSorting: false,
    }),
    columnHelper.accessor('prescriber_name', {
      header: 'Prescriber',
      size: DEFAULT_COLUMN_SIZE,
      enableSorting: true,
    }),
  ];

  const [searchValue, setSearchValue] = useState(initialSearchValue || '');

  // Sync searchValue when initialSearchValue changes (e.g., when search is cleared from parent)
  useEffect(() => {
    setSearchValue(initialSearchValue || '');
  }, [initialSearchValue]);

  // when the data is done loading, we want to reset the loading and data states
  useEffect(() => {
    if (loading) {
      setPageLoading(true);
    } else {
      setPageLoading(false);
      setDataState(data);
    }
  }, [loading, data]);

  const table = useReactTable({
    // this data state ensures that the data doesn't disappear while it's being refreshed
    data: dataState,
    columns,
    state: {
      pagination: {
        pageIndex: tableProps.pageIndex,
        pageSize: tableProps.perPage,
      },
      columnVisibility,
      sorting: tableProps.sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    pageCount: Math.ceil(tableProps.total / tableProps.perPage),
    manualPagination: true,
    manualSorting: true,
    columnResizeMode: 'onChange',
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: (updaterOrValue) => {
      const newValue = typeof updaterOrValue === 'function' ? updaterOrValue(tableProps.sorting) : updaterOrValue;
      tableProps.setSorting(newValue);
    },
  });

  const NoResultsComponent = ({
    table,
    initialFilterValues,
  }: {
    table: Table<RxTableResponseRow>;
    initialFilterValues?: RxFilterValues;
  }) => {
    return (
      <tr>
        <MergedRowItem colSpan={table.getAllColumns().length}>
          <TableNullState
            nullStateType={getRxTableNullStateType(initialFilterValues, searchValue, hasAnyPrescriptions)}
            onClearFilters={clearAllFilters}
            onClearSearch={onClearSearch}
            onAction={() => setShowSendPrescriptionModal(true)}
            tableName="Prescriptions Table"
          />
        </MergedRowItem>
      </tr>
    );
  };

  const downloadButton = (
    <>
      <Divider />
      <IconButton onClick={handleDownload} isLoading={downloadLoading} size="sm">
        <DownloadTray title="download" />
      </IconButton>
    </>
  );

  return (
    <TableContainer>
      <FilterBar>
        {initialSearchValue ? (
          <SearchResultsBanner searchTerm={initialSearchValue} onClearSearch={onClearSearch!} count={tableProps.total}>
            {downloadButton}
          </SearchResultsBanner>
        ) : (
          <FilterBarRow>
            <TableMultiselectFilter
              ref={officeFilterRef}
              options={tableProps.filters.offices}
              label={'Office'}
              initialValue={
                getDropdownInitialValueFromSearchParamsAndCookie(
                  initialFilterValues?.office,
                  authContext.user?.member_id || null,
                  'office_name',
                ) as MultiselectAutocompleteOption[]
              }
              onChange={(_, value: MultiselectAutocompleteOption[]) => {
                const selectedValuesList = value.map((valueItem) => valueItem.value);
                setPageLoading(true);
                tableProps.setOfficeFilter(selectedValuesList);
                updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'office_name');
              }}
              {...createTrackingProps({ elementName: 'Prescriptions Table: Office Filter', unmask: true })}
            />
            <TableMultiselectFilter
              ref={prescriberFilterRef}
              options={tableProps.filters.prescribers}
              label={'Prescriber'}
              initialValue={
                getDropdownInitialValueFromSearchParams(
                  authContext.user?.default_filters,
                  initialFilterValues?.prescriber,
                  'providers',
                  tableProps.filters.prescribers,
                ) as MultiselectAutocompleteOption[]
              }
              onChange={(_, value: MultiselectAutocompleteOption[]) => {
                const selectedValuesList = value.map((valueItem) => valueItem.value);
                setPageLoading(true);
                setPrescriberSelections(selectedValuesList);
                tableProps.setPrescriberFilter(selectedValuesList);
              }}
              {...createTrackingProps({ elementName: 'Prescriptions Table: Prescriber Filter', unmask: true })}
            />
            <TableMultiselectFilter
              ref={drugFilterRef}
              options={tableProps.filters.drugs}
              label={'Drug'}
              initialValue={initialDrugOptions}
              onChange={(_, value: MultiselectAutocompleteOption[]) => {
                const selectedValuesList = value.map((valueItem) => valueItem.value);
                setPageLoading(true);
                tableProps.setDrugNameFilter(selectedValuesList);
                updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'drug_name');
              }}
              {...createTrackingProps({ elementName: 'Prescriptions Table: Drug Filter', unmask: true })}
            />
            <TableMultiselectFilter
              ref={drugCategoryFilterRef}
              options={DRUG_CATEGORY_DROPDOWN_OPTIONS}
              label={'Drug category'}
              initialValue={
                getDropdownInitialValueFromSearchParamsAndCookie(
                  initialFilterValues?.drug_category,
                  authContext.user?.member_id || null,
                  'drug_category',
                  DRUG_CATEGORY_DROPDOWN_OPTIONS,
                ) as MultiselectAutocompleteOption[]
              }
              onChange={(_, value: MultiselectAutocompleteOption[]) => {
                const selectedValuesList = value.map((valueItem) => valueItem.value);
                setPageLoading(true);
                tableProps.setDrugCategoryFilter(selectedValuesList as DrugCategory[]);
                updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'drug_category');
              }}
              {...createTrackingProps({ elementName: 'Prescriptions Table: Drug Category Filter', unmask: true })}
            />
            <TableMultiselectFilter
              ref={statusFilterRef}
              options={STATUS_DROPDOWN_OPTIONS}
              label={'Status'}
              initialValue={
                getDropdownInitialValueFromSearchParams(
                  authContext.user?.default_filters,
                  initialFilterValues?.status,
                  undefined,
                  STATUS_DROPDOWN_OPTIONS,
                ) as MultiselectAutocompleteOption[]
              }
              onChange={(_, value: MultiselectAutocompleteOption[]) => {
                const selectedValuesList = value.map((valueItem) => valueItem.value);
                setPageLoading(true);
                tableProps.setStatusFilter(selectedValuesList as RxStatus[]);
              }}
              {...createTrackingProps({ elementName: 'Prescriptions Table: Status Filter', unmask: true })}
            />
            <DateFilter
              ref={dateFilterRef}
              startDate={initialFilterValues?.startDate || undefined}
              endDate={initialFilterValues?.endDate || undefined}
              onApply={({ startDate, endDate }) => {
                setPageLoading(true);
                tableProps.setDateRangeFilter({ startDate, endDate });
              }}
              onClear={() => {
                setPageLoading(true);
                tableProps.setDateRangeFilter({ startDate: undefined, endDate: undefined });
              }}
              {...createTrackingProps({ elementName: 'Prescriptions Table: Date Filter', unmask: true })}
            />
            <SaveFiltersButton
              $visible={showSaveButton}
              onClick={() => setShowSaveModal(true)}
              {...createTrackingProps({ elementName: 'Prescriptions Table: Save Filters Button' })}
            />

            <TableResultCount count={tableProps.total}>{downloadButton}</TableResultCount>
          </FilterBarRow>
        )}
      </FilterBar>

      <TableComponent role="tabpanel" id={tabPanelId} aria-labelledby={tabId}>
        <thead {...createTrackingProps({ unmask: true })}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeader
                  key={header.id}
                  onClick={
                    header.column.getCanSort()
                      ? () => {
                          setPageLoading(true);
                          header.column.toggleSorting();
                        }
                      : undefined
                  }
                  $width={`${header.getSize()}px`}
                  $sortable={header.column.getCanSort()}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <HeaderSortingIndicator
                      isSorted={header.column.getIsSorted()}
                      sortDirection={header.column.getIsSorted()}
                    />
                  )}
                </TableHeader>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {/* Let users know that if they're looking for a pending prescription, it won't show up in these search results */}
          {initialSearchValue && hasProcessingPrescriptions && (
            <tr>
              <td colSpan={table.getAllColumns().length} className="p-0">
                <div className="mb-1 mt-3 flex items-center gap-6 self-stretch rounded-medium border border-solid border-grayscale-100 bg-grayscale-50 p-3">
                  <span className="text-grayscale-700 text-body-small-dm-sans">
                    Pending prescriptions are not included in search results until they finish processing.
                  </span>
                </div>
              </td>
            </tr>
          )}
          {pageLoading ? (
            <LoadingComponent table={table} />
          ) : dataState.length === 0 ? (
            <NoResultsComponent table={table} initialFilterValues={initialFilterValues} />
          ) : (
            table.getRowModel().rows.map((row) => (
              <ClickableRow
                key={row.id}
                onClick={() => navigate(`/rxs/${row.original.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(`/rxs/${row.original.id}`);
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} $width={`${cell.column.getSize()}px`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </ClickableRow>
            ))
          )}
        </tbody>
      </TableComponent>
      {tableProps.total > 1 && (
        <Pagination
          table={table}
          goBack={() => {
            setPageLoading(true);
            tableProps.goBack();
          }}
          goForward={() => {
            setPageLoading(true);
            tableProps.goForward();
          }}
        />
      )}
      {showSaveModal && (
        <SaveFiltersModal
          onClose={() => setShowSaveModal(false)}
          filters={[
            {
              label: 'Prescriber',
              displayValues: prescriberSelections.map(
                (npi) => tableProps.filters.prescribers.find((p) => p.value === npi)?.description || npi,
              ),
              saveValues: prescriberSelections,
            },
          ]}
        />
      )}
      {showSendPrescriptionModal && (
        <SendFirstPrescriptionModal
          open={showSendPrescriptionModal}
          onClose={() => setShowSendPrescriptionModal(false)}
        />
      )}
    </TableContainer>
  );
};

export default RxTable;
